import type { StaffSession } from '@myfm/domain';
import { workspaceCode } from './workspace-catalogue';
import {featureWorkspacePage} from './feature-workspace-navigation';
import { selectedBuilding, withBuildingScope } from './building-scope';

/** My Work holds personal queues; every area of work sits under Buildings, scoped by the building switcher. */
const myWorkPaths=new Set(['/','/my-work','/work','/work/reviews','/work/team','/work/unassigned','/work/building-safety','/lenses','/dashboards','/approvals']);
export function primarySection(pathname:string,search=''){
 if(featureWorkspacePage(pathname)?.workspace==='administration')return undefined;
 if(pathname==='/portfolio'||pathname==='/buildings')return 'buildings';
 if(pathname.startsWith('/dashboards')||myWorkPaths.has(pathname)||pathname.startsWith('/work/questions'))return 'tasks';
 if(pathname==='/engines'||workspaceCode(pathname))return 'engines';
 if(pathname.startsWith('/admin')||pathname==='/audit')return undefined;
 if(selectedBuilding(pathname,search)||pathname.startsWith('/buildings'))return 'buildings';
 return undefined;
}

export function globalDestinations(session: StaffSession) {
  const building=session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
  return ['tasks','buildings','engines'].flatMap(id=>{
    const item=session.permittedDestinations.find(destination=>destination.id===id);
    if(!item)return [];
    return [{...item,label:id==='tasks'?'My day':id==='engines'?'Workspaces':'Buildings',href:withBuildingScope(id==='tasks'?'/':item.href,building)}];
  });
}

export function buildingDestinations(session: StaffSession, building: string) {
  if (!session.permittedDestinations.some(item => item.id === 'buildings')) return [];
  const definitions = [
    { id: 'overview', destination: 'buildings', label: 'Overview', href: `/buildings/${building}` },
    { id: 'people', destination: 'people', label: 'People & Properties', href: '/contacts' },
    { id: 'mail', destination: 'mail', label: 'Communications', href: '/communications' },
    { id: 'calendar', destination: 'calendar', label: 'Calendar', href: '/calendar' },
    { id: 'work', destination: 'work', label: 'Repairs & Work', href: '/work/all' },
    { id: 'governance', destination: 'buildings', label: 'Governance', href: `/buildings/${building}/governance` },
    { id: 'compliance', destination: 'compliance', label: 'Compliance', href: '/compliance' },
    { id: 'insurance', destination: 'insurance', label: 'Insurance', href: '/insurance' },
    { id: 'finance', destination: 'finance', label: 'Finance', href: '/finance/operations' },
    { id: 'assets', destination: 'buildings', label: 'Assets and systems', href: '/assets' },
    { id: 'documents', destination: 'documents', label: 'Documents', href: '/documents' },
    { id: 'building-settings', destination: 'buildings', label: 'Building settings', href: `/buildings/${building}/settings` },
  ];
  return definitions.filter(item => session.permittedDestinations.some(destination => destination.id === item.destination) && (item.id !== 'search' || session.capabilities.includes('contacts:search')) && (item.id !== 'governance' || session.capabilities.includes('governance:view')) && (item.id !== 'building-settings' || session.capabilities.includes('property-assets:manage')))
    .map(item => ({ ...item, href: withBuildingScope(item.href, building) }));
}

/** The same area tabs across all buildings; building-only areas (overview, governance, settings) are omitted.
 * The buildings register is reached from the building switcher and the primary Buildings link. */
export function areaTabs(session: StaffSession, building?: string) {
  if (building) return buildingDestinations(session, building);
  const definitions = [
    { id: 'people', destination: 'people', label: 'People & Properties', href: '/contacts' },
    { id: 'mail', destination: 'mail', label: 'Communications', href: '/communications' },
    { id: 'calendar', destination: 'calendar', label: 'Calendar', href: '/calendar' },
    { id: 'work', destination: 'work', label: 'Repairs & Work', href: '/work/all' },
    { id: 'compliance', destination: 'compliance', label: 'Compliance', href: '/compliance' },
    { id: 'insurance', destination: 'insurance', label: 'Insurance', href: '/insurance' },
    { id: 'finance', destination: 'finance', label: 'Finance', href: '/finance/operations' },
    { id: 'assets', destination: 'buildings', label: 'Assets and systems', href: '/assets' },
    { id: 'documents', destination: 'documents', label: 'Documents', href: '/documents' },
  ];
  return definitions.filter(item => session.permittedDestinations.some(destination => destination.id === item.destination));
}

/** One route map for section highlighting and safe switching; record identities never transfer. */
export function buildingSection(pathname:string,search=''):string|undefined {
 const feature=featureWorkspacePage(pathname);
 if(feature)return ({property_admin:'people',communications:'mail',operations_compliance:'operations',repairs:'work',suppliers:'work',assets:'assets',finance:'finance',governance:'governance'} as Record<string,string>)[feature.workspace];
 const query=new URLSearchParams(search);
 if(/^\/buildings\/[^/]+\/outcomes(?:\/[^/]+)?$/.test(pathname))return 'overview';
 if(/^\/buildings\/[^/]+\/(profile|authority-contacts|lease-handover|insurance|charging-vat|schedule-coverage|commercial-handover|spending-authority|authority-evidence|contractual-notices)$/.test(pathname))return 'overview';
 if(pathname.startsWith('/reports')||pathname.startsWith('/operations')||pathname==='/my-work')return 'operations';
 if(/^\/buildings\/[^/]+$/.test(pathname)||pathname==='/'||pathname==='/lenses'||pathname==='/dashboards')return 'overview';
 if(pathname.startsWith('/insurance'))return 'insurance';
 if(/\/building-safety\//.test(pathname)||pathname==='/work/building-safety'||pathname.startsWith('/compliance'))return 'compliance';
 if(/\/governance(?:\/decisions)?$/.test(pathname))return 'governance';
 if(/\/setup\/financials$/.test(pathname)||/\/legal-finance$/.test(pathname)||pathname.startsWith('/finance/')||pathname==='/admin/financial-settings')return 'finance';
 if(/\/mobilisation\/premises(?:\/|$)/.test(pathname))return 'people';
 if(/\/(setup|onboarding|genie)$/.test(pathname)&&pathname.startsWith('/buildings/')||/\/information$/.test(pathname)||/\/mobilisation\//.test(pathname))return 'overview';
 if(/\/settings$/.test(pathname)&&pathname.startsWith('/buildings/'))return 'building-settings';
 if(pathname==='/search')return 'search';
 if(pathname.startsWith('/admin/onboarding/'))return 'overview';
 if(pathname.startsWith('/records/')||pathname.startsWith('/contacts')||/\/properties(?:\/|$)/.test(pathname)||/^\/developments\/[^/]+\/contacts$/.test(pathname))return 'people';
 if(pathname.startsWith('/communications'))return 'mail';
 if(pathname.startsWith('/documents'))return 'documents';
 if(pathname.startsWith('/calendar'))return 'calendar';
 if(pathname.startsWith('/assets')||pathname.startsWith('/property-assets/')||pathname.startsWith('/buildings/areas/'))return 'assets';
 if(pathname==='/work/all'&&query.get('status')==='Procurement')return 'procurement';
 if(pathname.startsWith('/work/findings'))return 'remedials';
 if(pathname.startsWith('/work/actions'))return 'tasks';
 if(pathname==='/work'||pathname.startsWith('/work/')||/^\/buildings\/[^/]+\/work$/.test(pathname))return 'work';
 return undefined;
}
/** Secondary ownership does not change the underlying route/record classification. */
export function buildingNavigationSection(pathname:string,search=''){
 const section=buildingSection(pathname,search);
 return section==='remedials'?'compliance':section==='tasks'||section==='procurement'?'work':section;
}
export function switchedBuildingHref(pathname:string,building:string,search='',session?:StaffSession){
 if(pathname==='/portfolio')return withBuildingScope('/portfolio',building||undefined);
 if(pathname==='/engines'||/^\/engines\/[a-z_]+(?:\/(?:contributions|standards))?$/.test(pathname)){
  const tab=new URLSearchParams(search).get('tab');
  return withBuildingScope(pathname+(tab==='registers'||tab==='review'?'?tab='+tab:''),building||undefined);
 }
 if(buildingSection(pathname)==='operations'&&(!session||session.capabilities.includes('field-reports:view'))){
  const root=pathname.startsWith('/reports')?'/reports':pathname.startsWith('/operations/visits')?'/operations/visits':pathname.startsWith('/operations/programmes')?'/operations/programmes':pathname==='/my-work'?'/my-work':['/operations/attention','/operations/resources'].includes(pathname)?pathname:pathname.startsWith('/operations/supplier-visits')?'/operations/supplier-visits':'/reports';
  const next=new URLSearchParams();if(root===pathname)for(const key of ['view','category']){const value=new URLSearchParams(search).get(key);if(value)next.set(key,value);}
  return withBuildingScope(root+(next.size?'?'+next:''),building);
 }
 const section=buildingSection(pathname,search);
 const destinations=session?buildingDestinations(session,building):undefined;
 // Remedials are linked from Compliance but retain their existing Work permission.
 const authorised=section==='search'?session?.capabilities.includes('contacts:search'):section==='remedials'?session?.permittedDestinations.some(d=>d.id==='work'):destinations?.some(d=>d.id===buildingNavigationSection(pathname,search));
 if(session&&!authorised)return withBuildingScope(`/buildings/${building}`,building);
 if(/^\/buildings\/[^/]+\/outcomes(?:\/[^/]+)?$/.test(pathname))return withBuildingScope(building?`/buildings/${building}/outcomes`:'/buildings',building||undefined);
 const native=/^\/buildings\/[^/]+\/(information|settings|governance(?:\/decisions)?|legal-finance|work|building-safety\/(?:governance|competence|evidence-pack))$/.exec(pathname);
 const collections=['/work/reviews',
 '/work/team',
 '/work/unassigned',
 '/finance/documents',
 '/compliance/documents',
 '/work/documents',
 '/documents/ownership',
 '/documents/media/review',
 '/search',
 '/contacts',
 '/documents',
 '/documents/media',
 '/documents/review',
 '/documents/ingestion',
 '/communications',
 '/communications/lens',
 '/communications/review',
 '/calendar',
 '/assets',
 '/work',
 '/work/all',
 '/work/actions',
 '/work/findings',
 '/work/items',
 '/work/operating',
 '/work/overdue',
 '/work/awaiting-evidence',
 '/work/awaiting-verification',
 '/finance/accounts-payable',
 '/finance/payables',
 '/finance/service-charges',
 '/finance/reconciliation',
 '/finance/operations',
 '/compliance',
 '/compliance/obligations',
 '/compliance/setup',
 '/compliance/overdue',
 '/compliance/due-soon',
 '/compliance/awaiting-evidence',
 '/compliance/awaiting-verification',
 '/compliance/evidence-review',
 '/lenses',
 '/dashboards',
  '/insurance','/insurance/reconciliation'];
 const roots:Record<string,string>={insurance:'/insurance',people:'/contacts',documents:'/documents',mail:'/communications',calendar:'/calendar',assets:'/assets',work:'/work/all',tasks:'/work/actions',remedials:'/work/findings',finance:'/finance/operations',compliance:'/compliance',procurement:'/work/all?status=Procurement'};
 const target=native?`/buildings/${building}/${native[1]}`:collections.includes(pathname)?pathname:roots[section??'']??`/buildings/${building}`;
 const url=new URL(target,'https://staff.invalid');
 // Only presentation order/view survive an equivalent collection switch. Search,
 // page, record IDs, selections and return paths belong to the previous building.
 if(target===pathname)for(const key of ['sort','view','domain']){const value=new URLSearchParams(search).get(key);if(value)url.searchParams.set(key,value);}
 if(section==='procurement')url.searchParams.set('status','Procurement');
 return withBuildingScope(url.pathname+url.search,building);
}

export function buildingMatches(buildings: StaffSession['buildings'], query: string) {
  const words = query.trim().toLocaleLowerCase('en-GB').split(/\s+/).filter(Boolean);
  return buildings.filter(building => words.every(word => building.name.toLocaleLowerCase('en-GB').includes(word)));
}

/** Leaving a building returns to its area register, never a scoped record identity. */
export function portfolioHref(pathname:string,search=''){
 if(pathname==='/engines'||/^\/engines\/[a-z_]+$/.test(pathname))return pathname;
 if(['/work/reviews','/work/team','/work/unassigned'].includes(pathname))return pathname;
 if(buildingSection(pathname)==='operations')return switchedBuildingHref(pathname,'',search).replace(/[?&]building=$/,'');
 const roots:Record<string,string>={insurance:'/insurance',overview:'/buildings',information:'/buildings','building-settings':'/buildings',governance:'/buildings',people:'/contacts',mail:'/communications',documents:'/documents',finance:'/finance/operations',compliance:'/compliance',work:'/work/all',tasks:'/work/actions',remedials:'/work/findings',procurement:'/work/all?status=Procurement',calendar:'/calendar',assets:'/assets',search:'/search'};
 const section=buildingSection(pathname,search);
 const root=roots[section??''];
 if(!root)return pathname.startsWith('/admin')?'/admin':pathname==='/'?'/':'/buildings';
 const sameCollection=['/contacts','/communications','/communications/lens','/communications/review','/documents','/documents/media','/documents/review','/documents/ingestion','/finance/accounts-payable','/finance/payables','/finance/operations','/finance/service-charges','/finance/reconciliation','/compliance','/work','/work/all','/work/actions','/work/findings','/search','/calendar','/lenses','/insurance'].includes(pathname);
 return sameCollection?pathname+(section==='procurement'?'?status=Procurement':''):root;
}
